import { useNavigate } from "react-router-dom";
import { useState,useEffect} from "react";
import {useApi,useInitUsageLog,useSession,useErrorDialog} from "../../hooks";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import {DISPLAY_HEADER_ICON_BACK,PAGE_NAME_FEEDBACK,DISPLAY_PAGE_REGISTER_SUBJECTNAME,PAGE_NAME_REGISTER_SUBJECTNAME} from "../../constants/common_constants";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { InputSubjectName } from "../../features/components/select_model/ui_elements/InputSubjectName";
import { calcCurrentDatetime,calcUtcDatetime} from "../../utils/basis_util";
import {RegisterSubjectNameButton} from "../../features/components/register_subjectname/ui_elements/RegisterSubjectNameButton";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import {checkSubjectName,registerSearchHistoryInfo} from "../../features/api/dts_api_wrapper";

export const RegisterSubjectName = () => {

    const currentDatetime = calcCurrentDatetime(); //現在日時
    const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)

    // 案件名を格納, ページ読み込み時の日時を初期値として設定
    const [subjectName, setSubjectName] = useState<string>(currentDatetime);
    const [isShowConfirmDialog, setIsShowConfirmDialog] = useState<Boolean>(false);    // ポップアップ表示用
    const {dacLanguage,dacModel,dacMachineNumber,dacOccurrenceTiming,dacInferenceFactor,setDacSearchHistoryId,setDacSubjectName} = useDtsAppContext();

    //State、またはローカルストレージから言語を取得
    const userId =
        localStorage.getItem(
        `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
        ) || "";    // ユーザーID
    const model = dacModel || localStorage.getItem("model") || "";    // 型番
    const machineNumber = dacMachineNumber || localStorage.getItem("machineNumber") || "";    // 機番
    const occurrenceTiming = dacOccurrenceTiming ||  Number(localStorage.getItem("occurrenceTiming")) || NaN;    // 発生タイミング
    const language = dacLanguage || localStorage.getItem("language") || "en";    // 言語
    const inferenceFactorStr = dacInferenceFactor || localStorage.getItem("inferenceFactor") || "";    // 推定要因項目
    const phenomenon = JSON.parse(localStorage.getItem("selectedPhenomenonName") || "[]") || "";    // 現象
    const displayPage = DISPLAY_PAGE_REGISTER_SUBJECTNAME;

    // ページ遷移用
    const navigate = useNavigate();

    //言語切替用
    const { t } = useTranslation();

    const { handleApi } = useApi();

    // 利用ログ登録
    const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
    });

    // セッション情報チェック
    const { handleCheckSession } = useSession("navigateToTopPage");
    
    // エラーダイアログ
    const { showErrorDialog } = useErrorDialog();

    // 初期表示時 ログ登録
    const handleRegisterSubjectNameInit = async () =>{
        try{
            console.log("handleRegisterSubjectNameInit start.");
            
            //利用ログ登録
            await initUsageLog();
        } catch (err) {
            console.log(`[${displayPage}]err: `, err);
            // カテゴリ取得失敗時もこのエラー
            showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "navigateToTopPage",
            ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
        }
        
    }

    // 初期表示時に実行
    useEffect(() => {

        // セッションチェック
        handleCheckSession();
        handleRegisterSubjectNameInit();
        const handleStorageChange = (event: StorageEvent) => {
        if (
            event.key ===
            `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
        ) {
            const authUser = localStorage.getItem(
            `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
            );
            if (!authUser) {
            console.log(`[${displayPage}]err: userId is unknown`);
            showErrorDialog(
                t("共通エラーポップ.エラー"),
                t("共通エラーポップ.通信エラーが発生しました。"),
                "navigateToTopPage",
            ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
            }
        }
        };
        window.addEventListener("storage", handleStorageChange);
        return () => {
        window.removeEventListener("storage", handleStorageChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ダイアログ表示
    const showConfirmDialog = () => {
        setIsShowConfirmDialog(true);
    };

    // ダイアログ非表示
    const closeConfirmDialog = () => {
        setIsShowConfirmDialog(false);
    };

    // ダイアログにてOKボタン押下時
    const handleOnOk = async () => {
        try {
            // ダイアログ非表示
            setIsShowConfirmDialog(false);

            const registerDateTime = calcUtcDatetime();    //ボタン押下時の時間を検索日時とする

            //案件名重複チェック(登録ボタン押下時)
            const resultPushGo = await handleApi(
                checkSubjectName,
                userId,
                subjectName,
            );
            
            // エラーの場合
            if ("errorMessage" in resultPushGo) {
                console.log("errorMessage: ", resultPushGo.errorMessage);
                // 案件名重複の場合
                if (resultPushGo.errorMessage === "Duplicated SubjectName Error") {
                throw new Error("Duplicated SubjectName Error");
                } else {
                throw new Error("failed to check subjectName");
                }
            }

            // 案件名をContext+ローカルストレージに保存
            setDacSubjectName(subjectName);
            localStorage.setItem("subjectName",subjectName);

            //検索履歴情報登録
            const regResult = await handleApi(
                registerSearchHistoryInfo,
                userId,
                subjectName,
                model,
                occurrenceTiming,
                phenomenon,
                inferenceFactorStr,
                language,
                registerDateTime,
                machineNumber || undefined,
            );

            //検索履歴情報登録のエラーの場合
            if ("errorMessage" in regResult) {
                console.log("errorMessage: ", regResult.errorMessage);
                throw new Error("failed to register searchHistory Info");
            }
            
            //検索履歴関連の情報をContext+ローカルストレージに保存
            setDacSearchHistoryId(regResult.searchHistoryId);
            localStorage.setItem("searchHistoryId", regResult.searchHistoryId);

            // フィードバック画面に遷移
            navigate(`/${PAGE_NAME_FEEDBACK}`, {
                state: {
                from: PAGE_NAME_REGISTER_SUBJECTNAME
                }
            });

        } catch(err) {
            console.log("[registerSearchHistoryInfo Api err :", err);
            if (!userId || !model || !subjectName || !phenomenon) {
                showErrorDialog(
                t("共通エラーポップ.エラー"),
                t("共通エラーポップ.通信エラーが発生しました。"),
                "navigateToTopPage",
                ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
            } else if (
                err instanceof Error &&
                err.message === "Duplicated SubjectName Error"
            ) {
                showErrorDialog(
                t("共通エラーポップ.エラー"),
                t("型式選択画面.登録済みの案件名です。"),
                "navigateToSubjectNameRegister",
                ); //エラー", "登録済みの案件名です。(型式選択画面に遷移する)
            } else {
                showErrorDialog(
                t("共通エラーポップ.エラー"),
                t("共通エラーポップ.通信エラーが発生しました。"),
                ); //エラー", "通信エラーが発生しました。
            }
        }
    }

    return (
    <>
        <div className="flex flex-col h-svh overflow-hidden">
            {/* header */}
            <GeneralCommonHeader
            title={t("案件名登録画面.案件名登録")} //案件名登録
            displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
            onBtnClick={() => {
                navigate(-1);
            }}
            />
     
            {/* header以下 */}
            <div className="flex flex-col m-4 h-full overflow-hidden whitespace-nowrap">

                {/* 見出し */}
                <div className="mb-2">
                    <p className="px-1">{t("案件名登録画面.案件名を入力してください")}</p>
                </div>

                {/* 案件名 */}
                <div className="mb-2 flex items-center justify-center">
                    <p className="p-1">
                    {t("案件名登録画面.案件名")} :{/* 案件名： */}{" "}
                    </p>
                    <InputSubjectName
                    subjectName={subjectName}
                    setSubjectName={setSubjectName}
                    />
                </div>

                {/* 保存ボタン */}
                <div className="flex justify-center items-center mt-[35px]">
                    <RegisterSubjectNameButton onBtnClick={showConfirmDialog} disabled={!subjectName}/>
                </div>
            </div>

            {/* ダイアログ */}
            {isShowConfirmDialog && (
                <ConfirmDialog
                message={t("案件名登録画面.この案件名で登録しますか？")}
                onCancel={closeConfirmDialog}
                onOk={handleOnOk}
                ></ConfirmDialog>
            )}
        </div>
    </>);
};